import { forwardRef, useMemo } from "react";
import { Chart } from "ui";
import type { ChartProps } from "ui";
import { ChartCard } from "../../ChartCard";
import type { ErrorResponse } from "../../../../../hooks/useMoneyHashQuery";
import { useChartsFormat } from "./ChartsProvider";
import type { ValueFormat } from "./valueFormat";

type LineChartProps = ChartProps<"line">;

type LineChartCardProps = {
  title: string;
  subTitle?: string;
  description?: React.ReactNode;
  labels: LineChartProps["labels"];
  datasets: LineChartProps["datasets"];
  /** How the y-axis values and tooltip values are formatted. */
  format?: ValueFormat;
  /** Number of legend series visible by default; the rest start hidden. */
  initialVisibleDatasets?: number;
  className?: string;
  contentClassName?: string;
  headerAction?: React.ReactNode;
  queryStatus?: "loading" | "error" | "success";
  error?: ErrorResponse | null;
  retry?: () => void;
};

export const LineChartCard = forwardRef<HTMLDivElement, LineChartCardProps>(
  function LineChartCard(
    {
      title,
      subTitle,
      description,
      labels,
      datasets,
      format = "percentage",
      initialVisibleDatasets = 5,
      className,
      contentClassName = "pr-0 pl-4 min-h-80 h-auto",
      headerAction,
      queryStatus,
      error,
      retry,
    },
    ref
  ) {
    const { formatXLabel, getValueFormatters } = useChartsFormat();

    const { formatYLabel, formatValue } = useMemo(
      () => getValueFormatters(format),
      [getValueFormatters, format]
    );

    return (
      <ChartCard
        ref={ref}
        title={title}
        subTitle={subTitle}
        description={description}
        className={className}
        contentClassName={contentClassName}
        headerAction={headerAction}
        queryStatus={queryStatus}
        error={error}
        retry={retry}
      >
        <Chart
          type="line"
          labels={labels}
          datasets={datasets}
          className="h-auto"
        >
          <Chart.Canvas
            className="h-72"
            formatYLabel={formatYLabel}
            formatXLabel={formatXLabel}
            maxYValue={format === "percentage" ? 100 : undefined}
          />
          <Chart.Tooltip
            valueFormatter={formatValue}
            labelFormatter={formatXLabel}
          />
          <Chart.Legend
            variant="row"
            initialVisibleDatasets={initialVisibleDatasets}
          />
        </Chart>
      </ChartCard>
    );
  }
);
